import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import hearingsService, { CreateHearingDto } from '../../services/hearings.service';
import casesService, { Case } from '../../services/cases.service';
import AttachmentPanel from '../../components/AttachmentPanel';

const HearingForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);

  const [formData, setFormData] = useState<CreateHearingDto & { status?: string }>({
    case_id: 0,
    title: '',
    hearing_type: 'other',
    scheduled_date: '',
    scheduled_time: '',
    location: '',
    virtual_link: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const casesResult = await casesService.list({ limit: 100 });
      setCases(casesResult.data);

      if (isEdit && id) {
        const hearing = await hearingsService.getById(parseInt(id));
        setFormData({
          case_id: hearing.case_id,
          title: hearing.title,
          hearing_type: hearing.hearing_type,
          scheduled_date: hearing.scheduled_date.split('T')[0],
          scheduled_time: hearing.scheduled_time || '',
          location: hearing.location || '',
          virtual_link: hearing.virtual_link || '',
          notes: hearing.notes || '',
          status: hearing.status
        });
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.case_id || !formData.title || !formData.scheduled_date) {
      toast.error('Por favor complete los campos requeridos');
      return;
    }

    try {
      setSaving(true);

      const dataToSend = {
        ...formData,
        scheduled_time: formData.scheduled_time || undefined,
        location: formData.location || undefined,
        virtual_link: formData.virtual_link || undefined,
        notes: formData.notes || undefined
      };

      if (isEdit && id) {
        await hearingsService.update(parseInt(id), dataToSend);
        toast.success('Audiencia actualizada');
      } else {
        await hearingsService.create(dataToSend);
        toast.success('Audiencia creada');
      }

      navigate('/hearings');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar la audiencia');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="p-0 me-3" onClick={() => navigate('/hearings')}>
          <FiArrowLeft size={24} />
        </Button>
        <h1 className="h3 mb-0">{isEdit ? 'Editar Audiencia' : 'Nueva Audiencia'}</h1>
      </div>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Caso *</Form.Label>
                  <Form.Select
                    name="case_id"
                    value={formData.case_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Seleccione un caso</option>
                    {cases.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.case_number} - {c.client_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tipo de Audiencia</Form.Label>
                  <Form.Select
                    name="hearing_type"
                    value={formData.hearing_type}
                    onChange={handleChange}
                  >
                    <option value="initial">Audiencia Inicial</option>
                    <option value="evidence">Audiencia de Pruebas</option>
                    <option value="judgment">Audiencia de Juzgamiento</option>
                    <option value="conciliation">Audiencia de Conciliación</option>
                    <option value="other">Otra</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Título *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ej: Audiencia Inicial - Ejecutivo"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Fecha *</Form.Label>
                  <Form.Control
                    type="date"
                    name="scheduled_date"
                    value={formData.scheduled_date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Hora</Form.Label>
                  <Form.Control
                    type="time"
                    name="scheduled_time"
                    value={formData.scheduled_time}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>

              {isEdit && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status || ''}
                      onChange={handleChange}
                    >
                      <option value="scheduled">Programada</option>
                      <option value="completed">Realizada</option>
                      <option value="postponed">Aplazada</option>
                      <option value="cancelled">Cancelada</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Lugar</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Ej: Juzgado 5 Civil del Circuito"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Enlace Virtual</Form.Label>
                  <Form.Control
                    type="url"
                    name="virtual_link"
                    value={formData.virtual_link}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Observaciones, preparación, etc."
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <Button variant="secondary" onClick={() => navigate('/hearings')}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <FiSave className="me-2" />
                    {isEdit ? 'Actualizar' : 'Crear'} Audiencia
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Documentos adjuntos — solo visible en modo edición */}
      {isEdit && id && (
        <AttachmentPanel entityType="hearing" entityId={parseInt(id)} />
      )}
    </div>
  );
};

export default HearingForm;
